import { Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, ViewContainerRef } from '@angular/core';
import { MatchDetailsComponent } from '../match-details/match-details.component';
import { delay, filter, take, Subscription } from 'rxjs';
import { CandidateContactService, ContactDetail } from 'src/app/core/services/candidate-contact.service';
import { CopyToClipboardService } from '../../services/copy-to-clipboard.service';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { GA_EVENTS, GA_PROPS } from 'src/app/core/const.variables';
import { slideInOut } from '../../animations/slide-in-out.animation';
import { getWindow } from 'ssr-window';
import { AuthService } from 'src/app/core/auth/auth.service';
import { ApiService, RequirementLevel } from 'src/app/core/services/api.service';
import { UserProxy } from 'src/app/core/store/user/user.proxy';
import { DashboardProxy } from 'src/app/core/store/dashboard/dashboard.proxy';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { buildWhatsappUrl, getWhatsappPhoneNumber } from '../../utils/whatsapp-click-to-chat';
import { isSkillMatched } from '../../utils/skill-match';
import {
  getEmailEngagementStatusClass,
  getEmailEngagementStatusIconClass,
  getEmailEngagementStatusKey,
} from 'src/app/core/models/candidate-email-engagement.model';
import { CandidateProfileEventType } from 'src/app/core/services/recruiter-activity-analytics.service.type';
import { SalaryService } from '../../services/salary.service';

@Component({
  selector: 'vacancy-talent-info',
  templateUrl: 'vacancy-talent-info.component.html',
  styleUrls: ['vacancy-talent-info.component.scss'],
  animations: [ slideInOut ]
})
export class VacancyTalentInfoComponent implements OnInit, OnChanges, OnDestroy {
  viewingEmail = false;
  viewingPhone = false;
  contactError = '';
  copiedContact: 'email' | 'phone' | null = null;
  copyingContact: 'email' | 'phone' | null = null;
  private contactRequests = new Subscription();
  private contactUpdates?: Subscription;
  private whatsappRequest?: Subscription;

  @Input() isOtherMatch = false;
  @Input() showShadows = false;
  @Input() enableScroll = true;
  @Input() isVacancy = true;
  private _data = null;
  private _vacancySkillNames: Set<string> | null = null;
  // Flattened talent hard skills with vacancy matches moved to the front (original order otherwise kept).
  displayHardSkills = [];

  @Input() set data(value) {
    if (value && this.isImpersonating) {
      value = { ...value, isEmailViewed: true, isPhoneNumberViewed: true };
    }
    this._data = value;
    this.displayHardSkills = this.buildDisplayHardSkills();
  }

  get data() {
    return this._data;
  }

  private get isImpersonating(): boolean {
    return typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('Atalef:Impersonate');
  }

  @Input() publicView = false;
  @Input() guestMode = false;
  @Input() matchData = null;
  @Input() matchScore = null;
  @Input() actionsData = null;
  @Input() isApplyLoading = null;
  @Input() vacancyTitle = '';
  @Input() set vacancySkillNames(value: Set<string> | null) {
    this._vacancySkillNames = value;
    this.displayHardSkills = this.buildDisplayHardSkills();
  }

  get vacancySkillNames(): Set<string> | null {
    return this._vacancySkillNames;
  }
  @Output() onApply = new EventEmitter();
  @Output() onContact = new EventEmitter();
  @Output() onOpenSequenceHistory = new EventEmitter<{ email: string; candidateId: string }>();
  @Output() onAnalyticsEvent = new EventEmitter<CandidateProfileEventType>();
  public RecruiterActivityEventType = CandidateProfileEventType;
  skillsMust = [];
  skillsNice = [];
  skillsShouldHave = [];
  skillsCouldHave = [];
  skillsLowPriority = [];
  showSoftSkills = false;
  guestRegistration = null;
  guestScore = 25;
  validatedJobTitles = null;
  validatedSkills = null;
  downloadInProgress = false;
  isIncognito$ = this.userProxy.userIncognito$;
  isLoggedin = this.authService.isAuthenticatedSubject$.getValue();
  matchInProgress = false;
  validationRequiredSkills = [];
  popupMessage = '';
  showApplyPopup = false;
  showWhatsappPopup = false;
  whatsappMessage = '';
  @HostBinding('class.has-whatsapp-popup') get hasWhatsappPopup(): boolean {
    return this.showWhatsappPopup;
  }

  constructor(
    private viewContainerRef: ViewContainerRef,
    private host: ElementRef,
    private gaService: GoogleAnalyticsService,
    private authService: AuthService,
    private apiService: ApiService,
    private userProxy: UserProxy,
    private dashboardProxy: DashboardProxy,
    private router: Router,
    private translate: TranslateService,
    private contacts: CandidateContactService,
    private clipboard: CopyToClipboardService,
    public salaryService: SalaryService
  ) {
  }

  ngOnInit(): void {
    this.contactUpdates = this.contacts.revealed$.subscribe(({ vacancyId, talentId, contact }) => {
      if (this.isVacancy || this.data?.id !== talentId || this.data?.contactVacancyId !== vacancyId) return;
      // Keep the current object identity so its in-flight reveal callbacks remain valid.
      if (contact.detail === 'email') {
        this.data.email = contact.value;
        this.data.isEmailViewed = true;
      } else {
        this.data.phone = { countryCode: contact.countryCode, phoneNumber: contact.value };
        this.data.isPhoneNumberViewed = true;
      }
    });
    if (this.guestMode) { this.guestModeMatchScore(); }
    if (this.isVacancy && this.data && this.data.hardSkills && this.data.hardSkills.length) {
      const hardSkillsMust = [];
      this.data.hardSkills.map(item => item.skills.map(val => (val.isRequired ? val.isRequired : val.requirementLevel === RequirementLevel.MustHave) && hardSkillsMust.push(val)))
      const hardSkillsNice = [];
      this.data.hardSkills.map(item => item.skills.map(val => (val.isRequired ? !val.isRequired : val.requirementLevel === RequirementLevel.NiceToHave) && hardSkillsNice.push(val)))
      const hardSkillsShouldHave = [];
      this.data.hardSkills.map(item => item.skills.map(val => val.requirementLevel === RequirementLevel.ShouldHave && hardSkillsShouldHave.push(val)))
      const hardSkillsCouldHave = [];
      this.data.hardSkills.map(item => item.skills.map(val => val.requirementLevel === RequirementLevel.CouldHave && hardSkillsCouldHave.push(val)))
      const hardSkillsLowPriority = [];
      this.data.hardSkills.map(item => item.skills.map(val => val.requirementLevel === RequirementLevel.LowPriority && hardSkillsLowPriority.push(val)))
      this.skillsMust = hardSkillsMust;
      this.skillsNice = hardSkillsNice;
      this.skillsShouldHave = hardSkillsShouldHave;
      this.skillsCouldHave = hardSkillsCouldHave;
      this.skillsLowPriority = hardSkillsLowPriority;
    }
    
    this.data.requestedHardSkills?.forEach((request:any) => {
        this.validationRequiredSkills.push(request.skill);
    });
    
    this.popupMessage = this.validationRequiredSkills.length > 0 ? `${this.translate.instant('VacancyTalentInfo.HiringImproveMatch')} <br/>` +
      this.validationRequiredSkills.map(s => `• ${s}`).join('<br>') :
      `${this.translate.instant('VacancyTalentInfo.SkillAssessments')}`;

    if (this.data && (this.data.validatedJobTitles || this.data.validatedSkills)) {
      this.validatedJobTitles = this.data.validatedJobTitles.filter(val => val.skillName === this.data.jobTitle);
      this.validatedSkills = this.data.validatedSkills;
    }
    setTimeout(() => {
      if (this.data?.possibleToApply?.reason?.id === 'no_match') {
        this.dashboardProxy.matchCalculation$.pipe(take(1)).subscribe(calc => { 
          if (calc) {
            this.matchInProgress = true;
            this.dashboardProxy.matchCalculation$.pipe(filter(val => val), take(1)).subscribe((v) => {
              setTimeout(() => getWindow().location.reload(), 1000);
            });
          }
        });
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.contactRequests.unsubscribe();
      this.contactRequests = new Subscription();
      this.viewingEmail = this.viewingPhone = false;
      this.contactError = '';
      this.copiedContact = this.copyingContact = null;
      this.showWhatsappPopup = false;
    }
  }

  ngOnDestroy(): void {
    this.contactUpdates?.unsubscribe();
    this.contactRequests.unsubscribe();
  }

  async copyContact(detail: 'email' | 'phone'): Promise<void> {
    const target = this.data;
    if (this.copyingContact || this.viewingEmail || this.viewingPhone || (detail === 'email' ? target?.isEmailViewed === false : target?.isPhoneNumberViewed === false)) return;
    const value = detail === 'email' ? target?.email :
      ((target?.phone?.countryCode || '') + (target?.phone?.phoneNumber || ''));
    if (!value || value.includes('[anonymous]')) return;
    this.copyingContact = detail;
    this.contactError = '';
    try {
      const copied = await this.clipboard.copyText(value);
      if (this.data !== target) return;
      this.copiedContact = copied ? detail : null;
      if (!copied) this.contactError = 'Could not copy contact details. Please try again.';
    } catch {
      if (this.data === target) this.contactError = 'Could not copy contact details. Please try again.';
    } finally {
      if (this.data === target) this.copyingContact = null;
    }
  }

  viewContact(detail: ContactDetail): void {
    const email = detail === 'email';
    if (this.viewingEmail || this.viewingPhone || this.copyingContact || !this.data?.contactVacancyId) return;
    const target = this.data;
    this.contactError = '';
    if (email) this.viewingEmail = true;
    else this.viewingPhone = true;
    this.contactRequests.add(this.contacts.view(target.contactVacancyId, target.id, detail).subscribe({
      next: response => {
        if (this.data !== target) return;
        this.data = email
          ? { ...target, email: response.value, isEmailViewed: true }
          : { ...target, phone: { countryCode: response.countryCode, phoneNumber: response.value }, isPhoneNumberViewed: true };
        if (email) this.viewingEmail = false;
        else this.viewingPhone = false;
      },
      error: () => {
        if (this.data !== target) return;
        if (email) this.viewingEmail = false;
        else this.viewingPhone = false;
        this.contactError = 'Could not reveal contact details. Please try again.';
      },
    }));
  }

  isVacancySkill(skillName: string): boolean {
    return isSkillMatched(this._vacancySkillNames, skillName);
  }

  private buildDisplayHardSkills() {
    const skills = (this._data?.hardSkills || []).flatMap(branch => branch?.skills || []);
    if (!this._vacancySkillNames?.size) {
      return skills;
    }
    const matched = skills.filter(skill => this.isVacancySkill(skill?.name));
    const others = skills.filter(skill => !this.isVacancySkill(skill?.name));
    return [...matched, ...others];
  }

  getSkillData(branchName: string) {
  const skill = this.validatedSkills?.find(
    b => b.skillName.toLowerCase() === branchName.toLowerCase()
  );
  return skill;
}

  get uanVerification() {
    return this.data?.verification?.uan?.status ? this.data.verification.uan : null;
  }

  get uniqueUanReasons(): string[] {
    return Array.from(new Set((this.uanVerification?.reasons || []).filter(Boolean)));
  }

  getUanStatusKey(status: string): string {
    return status ? `EmploymentVerification.Statuses.${status}` : '';
  }

  getUanStatusClass(status: string): string {
    const classes = {
      Verified: 'verified',
      PartiallyVerified: 'partially-verified',
      NotVerified: 'not-verified',
      NotAvailable: 'not-available',
    };
    return classes[status] || '';
  }

  getWorkVerificationLabelKey(verification): string {
    if (!verification) {
      return '';
    }

    return verification.isVerified
      ? 'EmploymentVerification.VerifiedWithEpfo'
      : verification.reason
        ? this.getUanReasonLabel(verification.reason)
        : this.getUanStatusKey('NotVerified');
  }

  getWorkVerificationClass(verification): string {
    return verification?.isVerified ? 'verified' : 'warning';
  }

  getUanReasonLabel(reason: string): string {
    if (!reason) {
      return '';
    }

    const key = `EmploymentVerification.Reasons.${reason}`;
    const label = this.translate.instant(key);
    return label === key ? reason : label;
  }

  getEpfoCurrentEmployerText(employment): string {
    const employer = employment?.employer || '';
    const from = this.formatEpfoDate(employment?.from);

    if (employer && from) {
      return this.translate.instant('EmploymentVerification.EpfoCurrentEmployer', { employer, from });
    }
    if (employer) {
      return this.translate.instant('EmploymentVerification.EpfoCurrentEmployerNameOnly', { employer });
    }
    return this.translate.instant('EmploymentVerification.EpfoCurrentEmployerDatesOnly', { from });
  }

  getEpfoEmploymentText(employment): string {
    const employer = employment?.employer || '';
    const from = this.formatEpfoDate(employment?.from);
    const to = employment?.to
      ? this.formatEpfoDate(employment.to)
      : this.translate.instant('EmploymentVerification.Present');

    if (!from && !employment?.to) {
      return employer
        ? this.translate.instant('EmploymentVerification.EpfoNoDates', { employer })
        : this.translate.instant('EmploymentVerification.EpfoNoDatesAnonymous');
    }

    const dates = [from, to].filter(Boolean).join(' – ');
    return employer
      ? this.translate.instant('EmploymentVerification.Epfo', { employer, dates })
      : this.translate.instant('EmploymentVerification.EpfoDatesOnly', { dates });
  }

  getAdditionalEmployerText(employment): string {
    const employer = employment?.employer || '';
    const from = this.formatEpfoDate(employment?.from);
    const to = employment?.to
      ? this.formatEpfoDate(employment.to)
      : this.translate.instant('EmploymentVerification.Present');
    const dates = [from, to].filter(Boolean).join(' – ');

    return employer
      ? this.translate.instant('EmploymentVerification.EpfoAlsoShows', { employer, dates })
      : this.translate.instant('EmploymentVerification.EpfoAlsoShowsDatesOnly', { dates });
  }

  private formatEpfoDate(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }

  private guestModeMatchScore() {
    
    const generateAlternatingArray = (count) => {
      var numbers = [];
      for (var i = 0; i < count; i++) {
          numbers.push(i % 2 === 0 ? 90 : 84);
      }
      return numbers;
    }
    const numbersArray = generateAlternatingArray(100);
    numbersArray.map((n, i) => {
      setTimeout(() => this.guestScore = n, i * 700);
    });
  }

  contact(messageThreadId, vacancyId, talentId = null) {
    this.onContact.emit({ messageThreadId, vacancyId, talentId });
  }

  trackAnalyticsEvent(eventType: CandidateProfileEventType): void {
    this.onAnalyticsEvent.emit(eventType);
  }

  get hasValidWhatsappNumber(): boolean {
    const phone = this.data?.phone?.phoneNumber;
    return !!phone && !phone.includes('[anonymous]');
  }

  openWhatsappPopup(event?: Event): void {
    event?.stopPropagation();
    if (!this.hasValidWhatsappNumber) {
      return;
    }

    this.whatsappMessage = this.translate.instant('WhatsApp.DefaultMessage', {
      candidateName: this.data?.name || '',
      jobTitle: this.vacancyTitle || '',
    });
    this.showWhatsappPopup = true;
  }

  closeWhatsappPopup(): void {
    if (this.whatsappRequest && !this.whatsappRequest.closed) {
      this.whatsappRequest.unsubscribe();
      this.viewingPhone = false;
    }
    this.showWhatsappPopup = false;
  }

  openWhatsapp(): void {
    if (this.viewingEmail || this.viewingPhone || this.copyingContact || !this.showWhatsappPopup) return;
    if (this.data?.isPhoneNumberViewed === false) {
      const target = this.data;
      if (!target.contactVacancyId || !target.id) return;
      const message = this.whatsappMessage;
      this.viewingPhone = true;
      this.contactError = '';
      const request = this.contacts.view(target.contactVacancyId, target.id, 'whatsapp').subscribe({
        next: contact => {
          if (this.data !== target) return;
          this.data = { ...target, phone: { countryCode: contact.countryCode, phoneNumber: contact.value }, isPhoneNumberViewed: true };
          this.viewingPhone = false;
          const phone = this.getWhatsappPhoneNumber();
          if (!phone) {
            this.contactError = 'This phone number cannot be opened in WhatsApp.';
            return;
          }
          if (this.showWhatsappPopup) {
            getWindow().open(buildWhatsappUrl(phone, message), '_blank', 'noopener');
            this.showWhatsappPopup = false;
          }
        },
        error: () => {
          if (this.data !== target) return;
          this.viewingPhone = false;
          this.contactError = 'Could not reveal phone number. Please try again.';
        },
      });
      this.whatsappRequest = request;
      this.contactRequests.add(request);
      return;
    }
    const phoneNumber = this.getWhatsappPhoneNumber();
    if (!phoneNumber) {
      this.showWhatsappPopup = false;
      return;
    }
    getWindow().open(buildWhatsappUrl(phoneNumber, this.whatsappMessage), '_blank', 'noopener');
    this.showWhatsappPopup = false;
  }

  registerNow(e) {
    if (e) {
      this.authService.login();
    }
    this.guestRegistration = null;
  }

  applyRegistration(param) {
    this.guestRegistration = param;
  } 

  apply(matchId, vacancyId) {
    this.onApply.emit({ matchId, vacancyId });
  }

  goInternalVacancy(url) {
    this.gaService.gtag('event', GA_EVENTS.ExternalVcancyApply, { [GA_PROPS.Url]: url });
    getWindow().open(url, '_blank');
  }

  toggleSoftSkills() {
    this.showSoftSkills = !this.showSoftSkills;
  }

  showMatchDetails() {
    this.onAnalyticsEvent.emit(CandidateProfileEventType.CandidateMatchProfileOpened);
    const componentRef = this.viewContainerRef.createComponent(MatchDetailsComponent);
    componentRef.setInput('matchId', this.matchData.id);
    componentRef.setInput('isVacancy', this.isVacancy);
    componentRef.setInput('vacancySkillNames', this.vacancySkillNames);
    const analyticsSubscription = componentRef.instance.onAnalyticsEvent.subscribe(eventType => {
      this.onAnalyticsEvent.emit(eventType);
    });
    componentRef.instance.onClose.pipe(take(1)).subscribe(val => {
      analyticsSubscription.unsubscribe();
      componentRef.destroy();
    });
    this.host.nativeElement.parentElement.insertBefore(
      componentRef.location.nativeElement,
      this.host.nativeElement.parentElement.firstChild
    )
  }

  download(fileToDownload) {
    if (!fileToDownload?.fileUrl || fileToDownload.fileUrl === '[anonymous]') {
      return;
    }

    this.onAnalyticsEvent.emit(CandidateProfileEventType.CandidateCvDownloaded);
    const lastPart = fileToDownload.fileUrl.split('/').pop() || '';
    const extractedFileName = decodeURIComponent(lastPart.split('?')[0]);
    const file = {
      name: extractedFileName,
      type: fileToDownload.type,
      Url: fileToDownload.fileUrl
    }
    this.downloadInProgress = true;
    this.apiService.downloadFile(file).pipe(delay(1000)).subscribe((data) => {
      const downloadURL = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = file.name;
      link.click();
      setTimeout(() => {
        this.downloadInProgress = false;
        link.remove();
      }, 100);
    }); 
  }

    hasValidSocialMedia(): boolean {
    return (
      this.data &&
      this.data.company &&
      this.data.company.socialMedia &&
      this.data.company.socialMedia.length > 0 &&
      this.data.company.socialMedia.some(media => !!media.url)
    );
}
  onApplyClick() {
    if (this.data.applyLink) {
      this.goInternalVacancy(this.data.applyLink);
      return;
    }
    if (this.guestMode) {
      this.applyRegistration('apply');
      return;
    }
    this.showApplyPopup = true;
  }

  notifyResult(msg: any) {
    this.showApplyPopup = false;
    if (msg) {
      this.apply(
        this.matchData ? this.matchData.id : null,
        this.actionsData.vacancyId
      );
    }else if(msg == null){
      return;
    } 
    else {
       this.router.navigate(['talent/skills-validation']);
    }
  }

  private getWhatsappPhoneNumber(): string | null {
    return getWhatsappPhoneNumber({
      phoneNumber: this.data?.phone?.phoneNumber,
      countryCode: this.data?.phone?.countryCode,
      country: this.data?.location?.country,
    });
  }

  normalizeNullArray(array: any[]): { value: any[], isValid: boolean } {
    const filteredArray = array?.filter(item => item !== null && item !== undefined && item !== "");
    return { value: filteredArray, isValid: filteredArray?.length > 0 };
  }

  get showEmailEngagement(): boolean {
    return !this.isVacancy && !this.publicView && !this.guestMode && !!this.data?.hasEngagement;
  }

  get hasApplied(): boolean {
    return this.data?.isApplied === true;
  }

  get canOpenEmailStatusHistory(): boolean {
    return this.showEmailEngagement && !!this.data?.lastEventStatus && !!this.data?.isEmailViewed;
  }

  get showEmailEngagementStatus(): boolean {
    if (this.hasApplied && !this.isVacancy && !this.publicView && !this.guestMode) {
      return true;
    }
    return this.canOpenEmailStatusHistory;
  }

  get engagementStatusClass(): string {
    if (this.hasApplied) {
      return 'is-applied';
    }
    return getEmailEngagementStatusClass(this.data?.lastEventStatus);
  }

  get engagementStatusIconClass(): string {
    if (this.hasApplied) {
      return 'app-ico-mask__checked';
    }
    return getEmailEngagementStatusIconClass(this.data?.lastEventStatus);
  }

  get engagementStatusLabel(): string {
    if (this.hasApplied) {
      return 'VacanciesMatch.EmailEngagementHistory.StatusApplied';
    }
    return getEmailEngagementStatusKey(this.data?.lastEventStatus);
  }

  openSequenceHistoryFromEmail(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canOpenEmailStatusHistory) {
      return;
    }

    const email = (this.data?.email || '').trim();
    const candidateId = this.actionsData?.talentId || this.data?.id;
    if (!email || email.includes('[anonymous]') || !candidateId) {
      return;
    }

    this.onOpenSequenceHistory.emit({ email, candidateId });
  }

}
